import type { Page } from "./types";
import { mockFetch } from "../mock/transport";

const API_PREFIX = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";
let csrfToken = sessionStorage.getItem("opc.csrf") ?? "";

export class ApiError extends Error {
  constructor(public status: number, message: string, public code?: string, public requestId?: string) { super(message); }
}

function unwrap<T>(value: unknown): T {
  const record = value as Record<string, unknown> | null;
  return normalizeValue(record && "data" in record ? record.data : value) as T;
}

const responseAliases: Record<string, string> = {
  phone_masked: "masked_phone", policy_type: "type", target_audience: "audience",
  body_html: "content", external_url: "application_url", favorited: "is_favorited",
  liked: "is_liked", company_registered: "has_company", registration_place: "registration_location",
  settlement_mode: "settlement_type", review_opinion: "reviewer_comment",
  opinion: "comment", body: "content", category: "type", issuing_authority: "issuer",
};

function snakeKey(key: string) { return key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`); }
const opaquePayloadKeys = new Set(["schema", "schema_json", "ui_schema", "ui_schema_json", "extra_data"]);

function normalizeValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(normalizeValue);
  if (!value || typeof value !== "object") return value;
  const source = value as Record<string, unknown>; const result: Record<string, unknown> = {};
  Object.entries(source).forEach(([key, item]) => {
    const snake = snakeKey(key);
    result[responseAliases[snake] ?? snake] = opaquePayloadKeys.has(snake) ? item : normalizeValue(item);
  });
  if (result.id && result.name && (result.content_type || result.size) && !result.download_url) result.download_url = `${API_PREFIX}/files/${result.id}`;
  if (result.title && result.board_name) result.board = { id: result.board_id, name: result.board_name, slug: result.board_slug };
  if (result.title && result.author_id) result.author = { id: result.author_id, nickname: result.nickname, avatar_url: result.avatar_url, company: result.company_name, business_direction: result.business_direction };
  if (result.nickname && result.company_name && !result.company) result.company = result.company_name;
  if (result.title && Array.isArray(result.comments)) {
    const comments = result.comments as Array<Record<string, unknown>>;
    const roots = comments.filter((comment) => !comment.parent_id);
    result.comments = roots.map((root) => ({ ...root, replies: comments.filter((comment) => comment.parent_id === root.id || comment.root_id === root.id) }));
  }
  return result;
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  const method = (options.method ?? "GET").toUpperCase();
  if (options.body && !(options.body instanceof FormData) && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  if (!["GET", "HEAD", "OPTIONS"].includes(method) && csrfToken) headers.set("X-CSRF-Token", csrfToken);
  headers.set("Accept", "application/json");
  const response = await mockFetch(`${API_PREFIX}${path}`, { ...options, headers, credentials: "include" });
  const token = response.headers.get("X-CSRF-Token");
  if (token) { csrfToken = token; sessionStorage.setItem("opc.csrf", token); }
  const contentType = response.headers.get("content-type") ?? "";
  const payload = contentType.includes("json") ? await response.json() : response.status === 204 ? null : await response.text();
  if (!response.ok) {
    const body = payload as { error?: { message?: string; code?: string; requestId?: string }; message?: string; requestId?: string } | null;
    throw new ApiError(response.status, body?.error?.message ?? body?.message ?? `请求失败（${response.status}）`, body?.error?.code, body?.error?.requestId ?? body?.requestId ?? response.headers.get("X-Request-ID") ?? undefined);
  }
  const data = unwrap<T>(payload);
  const possibleToken = (data as { csrf_token?: string } | null)?.csrf_token;
  if (possibleToken) { csrfToken = possibleToken; sessionStorage.setItem("opc.csrf", possibleToken); }
  return data;
}

function camelKey(key: string) { return key.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase()); }
function requestValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(requestValue);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([key, item]) => {
    const snake = snakeKey(key);
    return [camelKey(key), opaquePayloadKeys.has(snake) ? item : requestValue(item)];
  }));
}
export const json = (value: unknown) => JSON.stringify(requestValue(value));

export function idempotencyKey() {
  const bytes = new Uint8Array(16);
  if (globalThis.crypto?.getRandomValues) globalThis.crypto.getRandomValues(bytes);
  else for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function normalizePage<T>(value: Page<T> | T[] | { items?: T[]; results?: T[]; total?: number; page?: number; page_size?: number; pagination?: { total?: number; page?: number; page_size?: number } }): Page<T> {
  if (Array.isArray(value)) return { items: value, total: value.length, page: 1, page_size: value.length || 20 };
  const source = value as { items?: T[]; results?: T[]; total?: number; page?: number; page_size?: number; pagination?: { total?: number; page?: number; page_size?: number }; total_pages?: number };
  const items = source.items ?? source.results ?? [];
  return { items, total: source.total ?? source.pagination?.total ?? items.length, page: source.page ?? source.pagination?.page ?? 1, page_size: source.page_size ?? source.pagination?.page_size ?? 20, total_pages: source.total_pages };
}

export function queryString(values: Record<string, string | number | boolean | undefined | null>) {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => { if (value !== undefined && value !== null && value !== "") params.set(key, String(value)); });
  const result = params.toString();
  return result ? `?${result}` : "";
}
